import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

const sendAnalyticsEvent = (
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	action: string,
	itemId: string,
	attributes: Record<string, any>,
) => {
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action,
			actionSubject: 'pageTree',
			actionSubjectId: itemId,
			source: 'pageTree',
			attributes,
		},
	}).fire();
};
/**
 * This hook is used to (potentially) preload the JS and data for the page tree item when the user
 * hovers over the page tree item for a certain amount of time.
 *
 * First stage is an experiment aimed to gather insights on the user behavior.
 */
export const usePreloadOnDelayedHover = ({
	itemId,
	isLive,
	contentType,
	isNav4Enabled,
}: {
	itemId: string;
	isLive: boolean;
	contentType: string;
	isNav4Enabled: boolean;
}) => {
	const preloadTimeout = StatsigConfigurations.getNumber('confluence-delayed-preload-timeout', 500);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const preloadAndLeaveRef = useRef<boolean>(false);
	const interactionTimeRef = useRef<number>(0);

	const onPreloadMouseEnter = useCallback(() => {
		if (!fg('confluence-delayed-preload-hover')) {
			return;
		}

		interactionTimeRef.current = performance.now();

		hoverTimeoutRef.current = setTimeout(() => {
			preloadAndLeaveRef.current = true;
			// NO-OP: with this we just measure user behavior, not actually preload the page yet
		}, preloadTimeout);

		// NOTE: dependency list is intentional, as future preloading function will depend on these values
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itemId, isLive, contentType]);

	const onPreloadMouseLeave = useCallback(() => {
		if (!fg('confluence-delayed-preload-hover')) {
			return;
		}

		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
			if (preloadAndLeaveRef.current) {
				preloadAndLeaveRef.current = false;
				// Here we want to know how many times the user has hovered over the page tree item
				// enough to start the preload, but then left without the navigation.
				sendAnalyticsEvent(createAnalyticsEvent, 'preloadAndLeave', itemId, {
					isLive,
					contentType,
					isNav4Enabled,
					preloadTimeout,
					leaveAfter: performance.now() - interactionTimeRef.current,
				});
			}
		}
	}, [isNav4Enabled, itemId, createAnalyticsEvent, isLive, contentType, preloadTimeout]);

	const onPreloadMouseDown = useCallback(() => {
		if (!fg('confluence-delayed-preload-hover')) {
			return;
		}

		preloadAndLeaveRef.current = false;
		sendAnalyticsEvent(createAnalyticsEvent, 'preloadAndClick', itemId, {
			isLive,
			contentType,
			isNav4Enabled,
			preloadTimeout,
			clickAfter: performance.now() - interactionTimeRef.current,
		});
	}, [createAnalyticsEvent, itemId, isLive, contentType, isNav4Enabled, preloadTimeout]);

	return {
		onPreloadMouseEnter,
		onPreloadMouseLeave,
		onPreloadMouseDown,
	};
};
